import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import StageCalculator from "../components/StageCalculator";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Save, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const STAGE_LABEL = {
  site_cleaning: "Sahənin təmizlənməsi",
  fill_layers: "Dolğu qatları",
  road_base: "Yol bazası",
  asphalt_coating: "Asfalt örtüyü",
};

export default function StageNew() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const [project, setProject] = useState(null);
  const [existingStages, setExistingStages] = useState([]);
  const [form, setForm] = useState({
    stage_type: "site_cleaning",
    length: "",
    width: "",
    thickness: "",
    k_factor: "1.25",
    density: "2.35",
    norm_id: "",
  });
  const [kmStart, setKmStart] = useState("");
  const [kmEnd, setKmEnd] = useState("");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/projects/${id}`),
      api.get("/stages", { params: { project_id: id } }),
    ])
      .then(([p, s]) => {
        setProject(p.data);
        setExistingStages(s.data || []);
      })
      .catch(() => {});
  }, [id]);

  // When km range provided, auto-fill length (m) from km × 1000
  useEffect(() => {
    if (kmStart !== "" && kmEnd !== "" && Number(kmEnd) > Number(kmStart)) {
      const L = (Number(kmEnd) - Number(kmStart)) * 1000;
      setForm((f) => ({ ...f, length: String(L) }));
    }
  }, [kmStart, kmEnd]);

  // Local pre-flight overlap check (still validated server-side)
  const overlapConflict = (() => {
    if (kmStart === "" || kmEnd === "") return null;
    const s = Number(kmStart);
    const e = Number(kmEnd);
    if (!(e > s)) return "km son km başlanğıcdan böyük olmalıdır";
    if (project && e > project.length_km) {
      return `Mərhələ uzunluğu layihənin (${project.length_km} km) hüdudunu keçə bilməz`;
    }
    const conflict = existingStages.find((stg) => {
      if (stg.stage_type !== form.stage_type) return false;
      if (stg.km_start == null || stg.km_end == null) return false;
      return s < stg.km_end && e > stg.km_start;
    });
    if (conflict) {
      return `Bu km aralığı üçün artıq "${STAGE_LABEL[form.stage_type] || form.stage_type}" mövcuddur (${conflict.km_start}-${conflict.km_end} km)`;
    }
    return null;
  })();

  const save = async () => {
    if (kmStart === "" || kmEnd === "") {
      toast.error("Km aralığı (başlanğıc və son) tələb olunur");
      return;
    }
    if (overlapConflict) {
      toast.error(overlapConflict);
      return;
    }
    if (!(form.width > 0)) {
      toast.error("En müsbət rəqəm olmalıdır");
      return;
    }
    if (form.stage_type !== "site_cleaning" && !(form.thickness > 0)) {
      toast.error("Qalınlıq müsbət rəqəm olmalıdır");
      return;
    }
    setSaving(true);
    try {
      await api.post("/stages", {
        project_id: id,
        title: title || undefined,
        notes,
        stage_type: form.stage_type,
        length: Number(form.length),
        width: Number(form.width),
        thickness: form.thickness ? Number(form.thickness) : null,
        k_factor: Number(form.k_factor || 1.25),
        density: Number(form.density || 2.35),
        norm_id: form.norm_id || null,
        km_start: Number(kmStart),
        km_end: Number(kmEnd),
      });
      toast.success("Mərhələ əlavə edildi");
      nav(`/projects/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    } finally {
      setSaving(false);
    }
  };

  // Visualise existing same-type stages on a horizontal km bar
  const sameTypeStages = existingStages
    .filter((s) => s.stage_type === form.stage_type && s.km_start != null)
    .sort((a, b) => a.km_start - b.km_start);
  const projectLen = project?.length_km || 1;
  const previewS = Number(kmStart);
  const previewE = Number(kmEnd);
  const previewValid =
    kmStart !== "" && kmEnd !== "" && previewE > previewS && !overlapConflict;

  return (
    <div className="space-y-6 max-w-6xl">
      <Link
        to={`/projects/${id}`}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
        data-testid="stage-back"
      >
        <ArrowLeft className="w-4 h-4" /> Layihəyə qayıt
      </Link>

      <div>
        <div className="label-overline mb-1">{t("new_stage")}</div>
        <h1 className="font-heading font-black text-3xl tracking-tight">
          Mərhələ Hesablaması
        </h1>
        <p className="text-slate-600 mt-2">
          DESN-2021-27 normalarına uyğun avtomatik resurs və dəyər hesablaması.
          {project && (
            <>
              {" "}
              Layihə uzunluğu:{" "}
              <span className="mono font-bold">{project.length_km} km</span>
            </>
          )}
        </p>
      </div>

      {/* KM range picker */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="km-range-panel">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Yol km aralığı</h3>
          <span className="mono text-[10px] uppercase tracking-wider text-slate-500">
            uzunluq avtomatik hesablanır
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="label-overline">Başlanğıc km</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max={projectLen}
              value={kmStart}
              onChange={(e) => setKmStart(e.target.value)}
              placeholder="0"
              data-testid="stage-km-start"
            />
          </div>
          <div>
            <Label className="label-overline">Son km</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              max={projectLen}
              value={kmEnd}
              onChange={(e) => setKmEnd(e.target.value)}
              placeholder={`${projectLen}`}
              data-testid="stage-km-end"
            />
          </div>
        </div>

        {/* Visual track */}
        <div data-testid="km-track">
          <div className="flex items-center justify-between mb-1 mono text-[10px] text-slate-400">
            <span>0</span>
            <span>{projectLen} km</span>
          </div>
          <div className="relative h-7 bg-slate-100 rounded">
            {sameTypeStages.map((s) => (
              <div
                key={s.id}
                className="absolute inset-y-0 bg-slate-400/70 border-l border-r border-slate-500"
                style={{
                  left: `${(s.km_start / projectLen) * 100}%`,
                  width: `${((s.km_end - s.km_start) / projectLen) * 100}%`,
                }}
                title={`${s.km_start}-${s.km_end} km — mövcud`}
              />
            ))}
            {previewS >= 0 && previewE > previewS && (
              <div
                className={`absolute inset-y-0 border-l-2 border-r-2 ${
                  previewValid
                    ? "bg-emerald-500/40 border-emerald-600"
                    : "bg-red-500/30 border-red-600"
                }`}
                style={{
                  left: `${Math.max(0, (previewS / projectLen) * 100)}%`,
                  width: `${Math.min(100, ((previewE - previewS) / projectLen) * 100)}%`,
                }}
                title={`Yeni: ${previewS}-${previewE} km`}
              />
            )}
          </div>
          <div className="flex items-center gap-4 mt-2 text-[10px] mono text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 bg-slate-400/70 rounded-sm inline-block" />
              Mövcud — {STAGE_LABEL[form.stage_type]}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 bg-emerald-500/40 border border-emerald-600 rounded-sm inline-block" />
              Yeni (uyğun)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 bg-red-500/30 border border-red-600 rounded-sm inline-block" />
              Kəsişir
            </span>
          </div>
        </div>

        {overlapConflict && (
          <div
            className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm"
            data-testid="km-overlap-error"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{overlapConflict}</span>
          </div>
        )}
      </div>

      <StageCalculator value={form} onChange={setForm} lengthReadOnly />

      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg">Əlavə məlumat</h3>
        <div>
          <Label className="label-overline">Başlıq (istəyə görə)</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="məs. 0-5 km dolğu"
            data-testid="stage-title"
          />
        </div>
        <div>
          <Label className="label-overline">Qeydlər</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            data-testid="stage-notes"
          />
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => nav(-1)}>
            {t("cancel")}
          </Button>
          <Button
            onClick={save}
            disabled={saving || !!overlapConflict}
            className="bg-[#004D99] hover:bg-[#003366]"
            data-testid="stage-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "..." : t("save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
